import { useCallback, useEffect, useMemo, useState } from "react";
import { Apple, ArrowRight, ExternalLink, Play, ShieldCheck } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { Button, Card, PageLoader, SamklangLoader, buttonVariants } from "@samklang/ui";
import { getOrganizationAccessIncludingInactive } from "@/services/organization-access";
import { createNativeAuthSessionUrl } from "@/services/native-auth";
import { APP_STORE_URL, GOOGLE_PLAY_URL, preferredMobileStore } from "@/services/store-links";
import { supabase } from "@/services/supabase";

type CallbackState = "checking" | "ready" | "invalid";

export function MagicLinkCallbackScreen() {
  const navigate = useNavigate();
  const [state, setState] = useState<CallbackState>("checking");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [nativeLaunchUrl, setNativeLaunchUrl] = useState<string>();
  const nativeRequested = new URL(window.location.href).searchParams.get("client") === "native";
  const soloOnboarding = new URL(window.location.href).searchParams.get("onboarding") === "solo";
  const preferredStore = useMemo(() => preferredMobileStore(window.navigator.userAgent), []);

  const ensureSoloOnboarding = useCallback(async () => {
    if (!soloOnboarding || !supabase) return;
    const { data, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !data.session) throw new Error("SESSION_MISSING");
    const apiBase = (import.meta.env.VITE_API_URL ?? "http://localhost:8787").replace(/\/$/, "");
    const response = await fetch(`${apiBase}/auth/solo-onboarding`, {
      method: "POST",
      headers: { Authorization: `Bearer ${data.session.access_token}` },
    });
    if (!response.ok) throw new Error("SOLO_ONBOARDING_FAILED");
  }, [soloOnboarding]);

  const continueInBrowser = useCallback(async () => {
    setBusy(true); setError("");
    try {
      await ensureSoloOnboarding();
      const organization = await getOrganizationAccessIncludingInactive();
      await navigate({ to: organization.access.active ? "/home" : "/subscription", replace: true });
    } catch {
      setError("Inloggningen lyckades, men kontot kunde inte öppnas. Försök igen eller kontakta Samklang.");
    } finally { setBusy(false); }
  }, [ensureSoloOnboarding, navigate]);

  useEffect(() => {
    if (!supabase) { setState("invalid"); return; }
    let active = true;
    void supabase.auth.getSession().then(({ data }) => { if (active) setState(data.session ? "ready" : "invalid"); });
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => { if (active && session) setState("ready"); });
    return () => { active = false; listener.subscription.unsubscribe(); };
  }, []);

  useEffect(() => {
    if (state === "ready" && !nativeRequested) void continueInBrowser();
  }, [continueInBrowser, nativeRequested, state]);

  useEffect(() => {
    if (state !== "ready" || !nativeRequested || !supabase) return;
    let active = true;
    setBusy(true);
    setError("");
    void (async () => {
      try {
        await ensureSoloOnboarding();
        const { data, error: sessionError } = await supabase.auth.getSession();
        if (sessionError || !data.session) throw new Error("SESSION_MISSING");
        const launchUrl = createNativeAuthSessionUrl({
          accessToken: data.session.access_token,
          refreshToken: data.session.refresh_token,
          destination: soloOnboarding ? "subscription" : "home",
        });
        if (active) setNativeLaunchUrl(launchUrl);
      } catch {
        if (active) setError("Appen kunde inte förberedas. Begär en ny länk eller fortsätt säkert i webbläsaren.");
      } finally {
        if (active) setBusy(false);
      }
    })();
    return () => { active = false; };
  }, [ensureSoloOnboarding, nativeRequested, soloOnboarding, state]);

  if (state === "checking" || (state === "ready" && !nativeRequested && !error)) return <PageLoader label={soloOnboarding ? "Förbereder ditt Solo-konto" : "Öppnar ditt Samklang-konto"} />;
  if (state === "invalid") return <section className="mx-auto max-w-2xl px-5 py-10"><Card padding="lg"><h1 className="font-display text-3xl font-semibold">Länken kan inte användas</h1><p className="mt-3 text-foreground-muted">Länken kan ha gått ut eller redan ha använts. Begär en ny säker inloggningslänk.</p><Button className="mt-6" onClick={() => void navigate({ to: "/login" })}>Till inloggningen <ArrowRight size={18} /></Button></Card></section>;

  const storeClass = buttonVariants({ variant: "outline", fullWidth: true });
  return <section className="mx-auto grid min-h-[calc(100dvh-150px)] w-full max-w-3xl items-center px-5 py-8"><Card padding="lg" className="text-center"><div className="mx-auto flex justify-center"><SamklangLoader variant="swirl" size="xl" label="Samklang" /></div><h1 className="mt-6 font-display text-3xl font-semibold">Du är säkert inloggad</h1><p className="mx-auto mt-3 max-w-xl text-foreground-muted">Öppna Samklang-appen på den här enheten. Om appen inte är installerad kan du hämta den eller fortsätta direkt i webbläsaren.</p><div className="mx-auto mt-5 flex max-w-xl gap-3 rounded-2xl bg-secondary-soft p-4 text-left text-sm"><ShieldCheck className="shrink-0 text-secondary" size={20} /><p>Inloggningslänken är personlig. Dela den aldrig med någon annan.</p></div>{error ? <p role="alert" className="mt-5 rounded-2xl bg-danger-soft p-4 text-sm text-danger">{error}</p> : null}<div className="mt-6 grid gap-3 sm:grid-cols-2">{nativeLaunchUrl ? <a className={buttonVariants({ fullWidth: true })} href={nativeLaunchUrl} onClick={() => { window.setTimeout(() => setError("Om appen inte öppnades kan du hämta den nedan eller fortsätta i webbläsaren."), 1_800); }}>Öppna appen <ExternalLink size={18} /></a> : <Button fullWidth loading={busy} loadingLabel="Förbereder appen" disabled>Förbereder appen</Button>}<Button fullWidth variant="secondary" disabled={busy} onClick={() => void continueInBrowser()}>Fortsätt i webbläsaren <ArrowRight size={18} /></Button></div><div className="mt-6 border-t border-border pt-6"><p className="mb-3 text-sm font-semibold text-foreground-muted">Har du inte appen?</p><div className={`grid gap-3 ${preferredStore ? "grid-cols-1" : "sm:grid-cols-2"}`}>{preferredStore !== "google_play" ? <a className={storeClass} href={APP_STORE_URL} rel="noreferrer"><Apple size={19} /> Hämta i App Store</a> : null}{preferredStore !== "app_store" ? <a className={storeClass} href={GOOGLE_PLAY_URL} rel="noreferrer"><Play size={19} /> Hämta på Google Play</a> : null}</div></div></Card></section>;
}
